import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Search, Download, Mail, Trash2, Eye, X, FileText, ArrowLeft } from "lucide-react";
import styles from "../hrms.module.scss";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";

const API = apiBaseUrl;

const authH = () => ({
  headers: {
    Authorization: `Bearer ${
      sessionStorage.getItem("platform_token") ||
      localStorage.getItem("platform_token") || ""
    }`,
  },
});

const fmt_date = (s) =>
  s ? new Date(s).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric"
  }) : "—";

// ── Download helper — uses html_content if available (browser print = exact preview) ──
// NOTE: the generated print window intentionally mirrors the printed PDF
// (fixed white paper palette) regardless of admin theme.
const downloadPdf = async (ol, setLoading) => {
  if (setLoading) setLoading(true);
  try {
    if (ol.html_content) {
      // Print the exact same HTML shown in the preview
      const w = window.open("", "_blank");
      w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Offer Letter — ${ol.candidate_name || "Candidate"}</title>
  <style>
    @media print { body { margin:0; } @page { margin:1.5cm; size:A4; } }
    body { margin:0; background:#fff; }
  </style>
</head>
<body>${ol.html_content}</body>
</html>`);
      w.document.close();
      w.onload = () => { w.focus(); w.print(); };
    } else {
      // Fallback: download backend-generated PDF
      const res = await axios.get(`${API}/offer-letters/${ol.id}/pdf`, {
        ...authH(), responseType: "blob",
      });
      const url  = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href     = url;
      link.download = `offer_${(ol.candidate_name || "candidate").replace(/\s+/g, "_")}_${ol.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  } catch (e) {
    alert("Download failed. Please try again.");
  } finally {
    if (setLoading) setLoading(false);
  }
};

// ══════════════════════════════════════════════════════════════════
// OFFER PREVIEW MODAL — shows the exact configured HTML
// ══════════════════════════════════════════════════════════════════
function OfferPreviewModal({ ol, onClose, onSent, onStatusChange }) {
  const { T } = useTheme();
  const STATUS_COLOR = {
    Draft:    T.muted,
    Sent:     T.cyan,
    Accepted: T.green,
    Declined: T.red,
  };

  const [view,       setView]       = useState("preview"); // "preview" | "details"
  const [sending,    setSending]    = useState(false);
  const [downloading,setDownloading]= useState(false);
  const [status,     setStatus]     = useState(ol.status);

  const handleSend = async () => {
    if (!ol.candidate_email) { alert("No candidate email on this offer letter."); return; }
    if (!window.confirm(`Send offer letter to ${ol.candidate_email}?`)) return;
    setSending(true);
    try {
      await axios.post(`${API}/offer-letters/${ol.id}/send-email`, {}, authH());
      setStatus("Sent");
      onSent(ol.id);
      alert(`✅ Offer letter sent to ${ol.candidate_email}`);
    } catch (e) {
      alert("❌ " + (e.response?.data?.detail || "Send failed"));
    } finally { setSending(false); }
  };

  const handleStatus = async (s) => {
    try {
      await axios.put(`${API}/offer-letters/${ol.id}/status`, { status: s }, authH());
      setStatus(s);
      onStatusChange(ol.id, s);
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 3000,
      display: "flex", flexDirection: "column",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: T.card, width: "100%", height: "100%",
        display: "flex", flexDirection: "column", fontFamily: "Outfit,sans-serif",
      }}>

        {/* ── Top bar ──────────────────────────────────────────── */}
        <div style={{
          padding: "12px 20px", background: T.card2,
          borderBottom: `1px solid ${T.cyan}1A`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button onClick={onClose}
              style={{ background: "none", border: "none", cursor: "pointer",
                color: T.muted, display: "flex", alignItems: "center", gap: 6,
                fontFamily: "Outfit,sans-serif", fontSize: 13 }}>
              <ArrowLeft size={15}/> Back
            </button>
            <div style={{ width: 1, height: 20, background: `${T.cyan}26` }}/>
            <div>
              <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase",
                letterSpacing: "0.1em" }}>OFFER LETTER</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>
                {ol.candidate_name} — {ol.position}
              </div>
            </div>
            {/* View tabs */}
            <div style={{ display: "flex", gap: 4, marginLeft: 12 }}>
              {[{ key:"preview", label:"Preview" }, { key:"details", label:"Details" }].map(t => (
                <button key={t.key} onClick={() => setView(t.key)}
                  style={{
                    padding: "5px 14px", borderRadius: 7, cursor: "pointer",
                    fontFamily: "Outfit,sans-serif", fontSize: 12, fontWeight: 700,
                    border: "none",
                    background: view === t.key
                      ? `linear-gradient(135deg,${T.cyan},${T.purple})`
                      : T.toggle_bg,
                    color: view === t.key ? "#fff" : T.muted,
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
            {/* Status */}
            <span style={{
              fontSize: 11.5, padding: "3px 10px", borderRadius: 20, fontWeight: 600,
              background: `${STATUS_COLOR[status] || T.muted}20`,
              color: STATUS_COLOR[status] || T.muted,
            }}>{status}</span>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={() => downloadPdf(ol, setDownloading)}
              disabled={downloading}
              style={{
                padding: "7px 16px", borderRadius: 8, cursor: "pointer",
                border: `1px solid ${T.cyan}40`, background: "none",
                color: T.cyan, fontFamily: "Outfit,sans-serif",
                fontSize: 12, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 6,
                opacity: downloading ? 0.6 : 1,
              }}>
              <Download size={13}/>
              {downloading ? "Generating…" : "Download PDF"}
            </button>

            {ol.candidate_email && (
              <button onClick={handleSend} disabled={sending}
                style={{
                  padding: "7px 16px", borderRadius: 8, cursor: "pointer",
                  border: "none",
                  background: `linear-gradient(135deg,${T.green},${T.cyan})`,
                  color: "#fff", fontFamily: "Outfit,sans-serif",
                  fontSize: 12, fontWeight: 700,
                  display: "flex", alignItems: "center", gap: 6,
                  opacity: sending ? 0.7 : 1,
                }}>
                <Mail size={13}/>
                {sending ? "Sending…" : "Send Email + PDF"}
              </button>
            )}

            <button onClick={onClose}
              style={{
                background: T.toggle_bg, border: "none",
                borderRadius: "50%", width: 30, height: 30, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: T.muted,
              }}>
              <X size={14}/>
            </button>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>

          {/* PREVIEW — exact configured HTML (fixed paper palette, not themed) */}
          {view === "preview" && (
            <div style={{ flex: 1, overflowY: "auto", background: "#e8e8e8", padding: 24 }}>
              {ol.html_content ? (
                <div style={{
                  background: "#fff", maxWidth: 800, margin: "0 auto",
                  borderRadius: 4, boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                  overflow: "hidden",
                }}
                  dangerouslySetInnerHTML={{ __html: ol.html_content }}
                />
              ) : (
                /* Fallback: no html_content saved */
                <div style={{
                  background: "#fff", maxWidth: 800, margin: "0 auto",
                  borderRadius: 4, padding: 48, boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                  fontFamily: "Georgia,serif", color: "#111",
                }}>
                  <h2 style={{ color: "#0D1B2E", marginBottom: 8 }}>{ol.company_name}</h2>
                  <p style={{ color: "#555" }}>{ol.company_address}</p>
                  <p>Dear <strong>{ol.candidate_name}</strong>,</p>
                  <p>We are pleased to offer you the position of <strong>{ol.position}</strong>.</p>
                  <table style={{ width:"100%", borderCollapse:"collapse", margin:"20px 0" }}>
                    {[
                      ["Position",     ol.position],
                      ["Department",   ol.department],
                      ["Joining Date", ol.joining_date],
                      ["CTC",          ol.ctc],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding:"8px 12px", border:"1px solid #ddd",
                          fontWeight:"bold", background:"#f5f5f5", width:"35%" }}>{k}</td>
                        <td style={{ padding:"8px 12px", border:"1px solid #ddd" }}>{v}</td>
                      </tr>
                    ))}
                  </table>
                  <p style={{ color:"#888", fontSize:13 }}>
                    Please confirm your acceptance
                    {ol.accept_deadline ? ` by ${ol.accept_deadline}` : ""}.
                  </p>
                  <br/>
                  <p><strong>{ol.hr_name}</strong><br/>{ol.hr_designation}</p>
                </div>
              )}
            </div>
          )}

          {/* DETAILS — metadata + status management */}
          {view === "details" && (
            <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
              <div style={{
                maxWidth: 560, margin: "0 auto",
                background: T.card2, border: `1px solid ${T.cyan}1A`,
                borderRadius: 12, overflow: "hidden",
              }}>
                <div style={{
                  padding: "16px 20px",
                  borderBottom: `1px solid ${T.cyan}14`,
                  fontSize: 12, fontWeight: 700, color: T.cyan,
                  textTransform: "uppercase", letterSpacing: "0.08em",
                }}>
                  Offer Details
                </div>
                <div style={{ padding: "4px 0" }}>
                  {[
                    ["Candidate",      ol.candidate_name],
                    ["Email",          ol.candidate_email || "—"],
                    ["Position",       ol.position],
                    ["Department",     ol.department],
                    ["Joining Date",   ol.joining_date],
                    ["Annual CTC",     ol.ctc],
                    ["Deadline",       ol.accept_deadline || "—"],
                    ["Company",        ol.company_name],
                    ["HR Signatory",   ol.hr_name],
                    ["Generated On",   fmt_date(ol.created_at)],
                    ["Email Sent At",  ol.email_sent_at ? fmt_date(ol.email_sent_at) : "Not sent yet"],
                  ].map(([k, v], i) => (
                    <div key={k} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "9px 20px", fontSize: 13,
                      background: i % 2 === 0 ? T.toggle_bg : "transparent",
                    }}>
                      <span style={{ color: T.muted }}>{k}</span>
                      <span style={{ color: T.text, fontWeight: 500,
                        textAlign: "right", maxWidth: 280 }}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* Status update */}
                <div style={{
                  padding: "16px 20px",
                  borderTop: `1px solid ${T.cyan}14`,
                }}>
                  <div style={{ fontSize: 11, color: T.muted, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                    Update Status
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["Draft","Sent","Accepted","Declined"].map(s => (
                      <button key={s} onClick={() => handleStatus(s)}
                        style={{
                          padding: "5px 14px", borderRadius: 20, cursor: "pointer",
                          fontFamily: "Outfit,sans-serif", fontSize: 12, fontWeight: 600,
                          border: "none",
                          background: status === s
                            ? `${STATUS_COLOR[s]}30` : T.toggle_bg,
                          color: status === s ? STATUS_COLOR[s] : T.muted,
                          outline: status === s ? `1px solid ${STATUS_COLOR[s]}60` : "none",
                        }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MAIN LIST PAGE
// ══════════════════════════════════════════════════════════════════
export default function OfferLetters() {
  const { T } = useTheme();
  const STATUS_COLOR = {
    Draft:    T.muted,
    Sent:     T.cyan,
    Accepted: T.green,
    Declined: T.red,
  };

  const [offers,    setOffers]    = useState([]);
  const [stats,     setStats]     = useState({});
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [statusF,   setStatusF]   = useState("");
  const [viewOffer, setViewOffer] = useState(null);
  const [deleting,  setDeleting]  = useState(null);
  const [dlId,      setDlId]      = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/offer-letters/`,      authH()),
        axios.get(`${API}/offer-letters/stats`, authH()),
      ]);
      setOffers(listRes.data  || []);
      setStats(statsRes.data  || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => offers.filter(o => {
    const q = search.toLowerCase();
    const matchS = !search || [o.candidate_name, o.position, o.department, o.candidate_email]
      .some(v => (v || "").toLowerCase().includes(q));
    const matchF = !statusF || o.status === statusF;
    return matchS && matchF;
  }), [offers, search, statusF]);

  const handleSent = (id) =>
    setOffers(prev => prev.map(o =>
      o.id === id ? { ...o, status:"Sent", email_sent_at: new Date().toISOString() } : o
    ));

  const handleStatusChange = (id, status) =>
    setOffers(prev => prev.map(o => o.id === id ? { ...o, status } : o));

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this offer letter?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/offer-letters/${id}`, authH());
      setOffers(prev => prev.filter(o => o.id !== id));
      if (viewOffer?.id === id) setViewOffer(null);
    } catch (e) { alert("Delete failed"); }
    finally { setDeleting(null); }
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>RECRUITMENT</div>
          <h2>Offer Letters</h2>
          <p>View, download and send generated offer letters</p>
        </div>
      </div>

      {/* Stats */}
      <div className={`${styles.statsGrid} ${styles.cols5}`}>
        {[
          { label:"Total",    val: stats.total    || 0, color:"cyan"   },
          { label:"Draft",    val: stats.draft    || 0, color:"amber"  },
          { label:"Sent",     val: stats.sent     || 0, color:"purple" },
          { label:"Accepted", val: stats.accepted || 0, color:"green"  },
          { label:"Declined", val: stats.declined || 0, color:"red"    },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className={styles.card}>
        {/* Filters */}
        <div style={{ display:"flex", gap:10, padding:"14px 20px",
          borderBottom:`1px solid ${T.border}`, flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, minWidth:200 }}>
            <Search size={13} style={{ position:"absolute", left:10, top:"50%",
              transform:"translateY(-50%)", color:T.dim }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search candidate, position…"
              style={{ width:"100%", background:T.inp_bg,
                border:`1px solid ${T.border}`, borderRadius:8,
                color:T.text, fontSize:13, padding:"7px 12px 7px 30px",
                outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
          </div>
          <select value={statusF} onChange={e => setStatusF(e.target.value)}
            style={{ background:T.inp_bg,
              border:`1px solid ${T.border}`, borderRadius:8,
              color:T.text, fontSize:13, padding:"7px 12px",
              outline:"none", fontFamily:"Outfit,sans-serif" }}>
            <option value="" style={{background:T.opt_bg}}>All Status</option>
            {["Draft","Sent","Accepted","Declined"].map(s =>
              <option key={s} value={s} style={{background:T.opt_bg}}>{s}</option>
            )}
          </select>
        </div>

        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Position</th>
                  <th>Department</th>
                  <th>CTC</th>
                  <th>Joining</th>
                  <th>Status</th>
                  <th>Generated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr className={styles.emptyRow}>
                    <td colSpan={8}>No offer letters found</td>
                  </tr>
                ) : filtered.map(o => (
                  <tr key={o.id}>
                    <td>
                      <div style={{ fontWeight:500 }}>{o.candidate_name}</div>
                      {o.candidate_email && (
                        <div style={{ fontSize:11, color:T.muted }}>{o.candidate_email}</div>
                      )}
                    </td>
                    <td style={{ color:T.cyan, fontSize:13 }}>{o.position}</td>
                    <td style={{ fontSize:13 }}>{o.department}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace",
                      fontSize:12, color:T.green }}>{o.ctc}</td>
                    <td style={{ fontSize:12, color:T.muted }}>{o.joining_date || "—"}</td>
                    <td>
                      <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20,
                        fontWeight:600,
                        background:`${STATUS_COLOR[o.status] || T.muted}20`,
                        color: STATUS_COLOR[o.status] || T.muted }}>
                        {o.status}
                      </span>
                    </td>
                    <td style={{ fontSize:12, color:T.muted }}>{fmt_date(o.created_at)}</td>
                    <td>
                      <div style={{ display:"flex", gap:5 }}>
                        {/* View — opens full-screen preview of configured HTML */}
                        <button className={styles.btnSm}
                          onClick={() => setViewOffer(o)}
                          title="View offer letter">
                          <Eye size={12}/> View
                        </button>
                        {/* Download PDF */}
                        <button className={styles.btnSm}
                          onClick={() => downloadPdf(o, (v) => setDlId(v ? o.id : null))}
                          disabled={dlId === o.id}
                          title="Download PDF">
                          <Download size={12}/>
                          {dlId === o.id ? "…" : ""}
                        </button>
                        {/* Delete */}
                        <button className={styles.btnSm}
                          onClick={() => handleDelete(o.id)}
                          disabled={deleting === o.id}
                          style={{ color:T.red }}
                          title="Delete">
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Full-screen preview modal */}
      {viewOffer && (
        <OfferPreviewModal
          ol             = {viewOffer}
          onClose        = {() => setViewOffer(null)}
          onSent         = {handleSent}
          onStatusChange = {handleStatusChange}
        />
      )}
    </div>
  );
}
